import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useMyPaymentProofs, useCancelPaymentProof } from '@/hooks/usePaymentProofs'
import { useUserEnrollments } from '@/hooks/useEnrollments'
import { PaymentProofCard } from '@/components/payment/PaymentProofCard'
import { PaymentProofUploadForm } from '@/components/payment/PaymentProofUploadForm'
import type { PaymentProof } from '@/types/paymentProof'
import { getPaymentConfig } from '@/config/payment'

export const PaymentHistoryPage: React.FC = () => {
  const { user } = useAuth()
  const { data: proofs, isLoading, error } = useMyPaymentProofs()
  const { data: enrollmentsRes } = useUserEnrollments(user?.id || '', { status: 'pending_payment' })
  const enrollments = enrollmentsRes?.data
  const cancelMutation = useCancelPaymentProof()

  const [showUploadForm, setShowUploadForm] = useState(false)
  const [selectedEnrollment, setSelectedEnrollment] = useState<{ id: string; courseName: string; amount: number } | null>(null)
  const [detailProof, setDetailProof] = useState<PaymentProof | null>(null)
  const [bankConfig, setBankConfig] = useState<{ bankName: string; accountNumber: string; accountHolderName: string } | null>(null)

  React.useEffect(() => {
    getPaymentConfig().then(setBankConfig).catch(() => {})
  }, [])

  const pendingProofs = proofs?.filter((p) => p.status === 'pending') || []
  const historyProofs = proofs?.filter((p) => p.status !== 'pending') || []

  // Enrollments with pending_payment that don't already have a pending proof
  const pendingProofEnrollmentIds = new Set(pendingProofs.map((p) => p.enrollmentId))
  const uploadableEnrollments = enrollments?.filter(
    (e) => !pendingProofEnrollmentIds.has(e.id)
  ) || []

  const handleCancel = (proofId: string) => {
    if (!confirm('Yakin ingin membatalkan bukti pembayaran ini?')) return
    cancelMutation.mutate(proofId, {
      onError: (error: Error) => {
        alert('Gagal membatalkan bukti pembayaran: ' + (error.message || 'Terjadi kesalahan'))
      }
    })
  }

  const handleOpenUpload = (enrollment: { id: string; courseName: string; amount: number }) => {
    setSelectedEnrollment(enrollment)
    setShowUploadForm(true)
  }

  const handleUploadSuccess = () => {
    setShowUploadForm(false)
    setSelectedEnrollment(null)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-24 md:pt-32 pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 dark:bg-white/10 rounded w-48" />
            <div className="h-24 bg-gray-200 dark:bg-white/10 rounded-xl" />
            <div className="h-24 bg-gray-200 dark:bg-white/10 rounded-xl" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-24 md:pt-32 pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 p-4 rounded-xl text-sm border border-red-200 dark:border-red-500/20">
            Gagal memuat data: {error.message}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-24 md:pt-32 pb-32">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="relative mb-8 p-6 bg-slate-900 dark:bg-white rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10">
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/20 to-transparent opacity-50" />
          <div className="relative z-10">
            <h1 className="text-2xl md:text-3xl font-bold text-white dark:text-slate-900 tracking-tight leading-none">
              Riwayat <span className="text-primary">Pembayaran</span>
            </h1>
            <p className="text-slate-400 dark:text-slate-500 text-sm font-medium mt-2">
              Kelola bukti pembayaran manual Anda
            </p>
          </div>
        </div>

        {/* Uploadable Enrollments — courses that need payment proof */}
        {uploadableEnrollments.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-3">
              Perlu Upload Bukti
            </h2>
            <div className="space-y-3">
              {uploadableEnrollments.map((enrollment) => (
                <div
                  key={enrollment.id}
                  className="bg-white dark:bg-surface-card rounded-xl border border-amber-200 dark:border-amber-500/20 p-4 flex items-center justify-between"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                      {enrollment.course?.title || 'Course'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-text-muted mt-0.5">
                      Rp {enrollment.originalPrice.toLocaleString('id-ID')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleOpenUpload({
                      id: enrollment.id,
                      courseName: enrollment.course?.title || 'Course',
                      amount: enrollment.originalPrice,
                    })}
                    className="ml-4 px-4 py-2 bg-primary text-white text-xs font-bold rounded-lg hover:opacity-90 transition-all shadow-sm shrink-0"
                  >
                    Upload Bukti
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {proofs?.length === 0 && uploadableEnrollments.length === 0 ? (
          <div className="bg-white dark:bg-surface-card rounded-xl shadow-sm border border-gray-200 dark:border-white/5 p-8 text-center">
            <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-white/20 mb-3">receipt_long</span>
            <p className="text-gray-500 dark:text-text-muted text-sm">Belum ada riwayat pembayaran</p>
          </div>
        ) : (
          <>
            {pendingProofs.length > 0 && (
              <div className="mb-6">
                <h2 className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-3">
                  Menunggu Verifikasi
                </h2>
                <div className="space-y-3">
                  {pendingProofs.map((proof) => (
                    <PaymentProofCard
                      key={proof.id}
                      proof={proof}
                      onViewDetail={() => setDetailProof(proof)}
                      onCancel={handleCancel}
                    />
                  ))}
                </div>
              </div>
            )}

            {historyProofs.length > 0 && (
              <div>
                <h2 className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-3">
                  Riwayat
                </h2>
                <div className="space-y-3">
                  {historyProofs.map((proof) => (
                    <PaymentProofCard
                      key={proof.id}
                      proof={proof}
                      onViewDetail={() => setDetailProof(proof)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload Form Modal */}
      {showUploadForm && selectedEnrollment && (
        <PaymentProofUploadForm
          enrollmentId={selectedEnrollment.id}
          courseName={selectedEnrollment.courseName}
          amount={selectedEnrollment.amount}
          targetBankConfig={bankConfig}
          onSuccess={handleUploadSuccess}
          onCancel={() => { setShowUploadForm(false); setSelectedEnrollment(null) }}
        />
      )}

      {/* Detail Modal */}
      {detailProof && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDetailProof(null)}>
          <div className="bg-white dark:bg-surface-card rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Detail Bukti Pembayaran</h2>
                <button onClick={() => setDetailProof(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-text-muted">Status</span>
                  <span className={`font-bold ${
                    detailProof.status === 'pending' ? 'text-amber-600' :
                    detailProof.status === 'approved' ? 'text-emerald-600' :
                    detailProof.status === 'rejected' ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {detailProof.status === 'pending' ? 'Menunggu Verifikasi' :
                     detailProof.status === 'approved' ? 'Disetujui' :
                     detailProof.status === 'rejected' ? 'Ditolak' : 'Kedaluwarsa'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-text-muted">Jumlah</span>
                  <span className="font-bold text-gray-900 dark:text-white">Rp {detailProof.amountPaid.toLocaleString('id-ID')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-text-muted">Bank</span>
                  <span className="text-gray-900 dark:text-white">{detailProof.bankName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-text-muted">No. Rekening</span>
                  <span className="text-gray-900 dark:text-white font-mono">{detailProof.accountNumber}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-text-muted">Atas Nama</span>
                  <span className="text-gray-900 dark:text-white">{detailProof.accountHolderName}</span>
                </div>
                {detailProof.notes && (
                  <div className="pt-2 border-t border-gray-100 dark:border-white/5">
                    <p className="text-xs text-gray-500 dark:text-text-muted mb-1">Catatan</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{detailProof.notes}</p>
                  </div>
                )}
                {detailProof.rejectionReason && (
                  <div className="pt-2 border-t border-gray-100 dark:border-white/5">
                    <p className="text-xs text-red-500 mb-1">Alasan Penolakan</p>
                    <p className="text-sm text-red-600 dark:text-red-400">{detailProof.rejectionReason}</p>
                  </div>
                )}
                {detailProof.proofFileUrl && (
                  <div className="pt-2 border-t border-gray-100 dark:border-white/5">
                    <a
                      href={detailProof.proofFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
                    >
                      <span className="material-symbols-outlined text-lg">open_in_new</span>
                      Lihat Bukti Transfer
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
